import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { useLocation, useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Switch,
} from "@/components/ui";
import { useIsMobile } from "@/hooks/use-mobile";
import { MoreVertical, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { routes } from "@/routes/utils";
import { useDeletePropertyZoneMutation, useGetPropertyZones } from "@/services";
import { toast } from "@/components/Toast";
import { useQueryClient } from "@tanstack/react-query";
import { ROLE_BOM } from "@/lib/utils";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

const usePropertyZone = () => {
  const location = useLocation();
  const isOwner = location?.pathname?.includes("owner");
  const isMobile = useIsMobile();
  const [globalFilter, setGlobalFilter] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deletedRow, setDeletedRow] = useState<any>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: propertyZones, isLoading: isLoadingPropertyZones } =
    useGetPropertyZones();

  const masterDataPermission = useSelector((state: RootState) => state.currentData.masterDataPermission);
  const currentRole = useSelector((state: RootState) => state.currentData.currentRole);
  const isCamBom = location.pathname.startsWith("/cam") || location.pathname.startsWith("/bom") || (location.pathname.startsWith("/owner") && currentRole == ROLE_BOM);

  const hasAddPermission = useMemo(() => isCamBom ? masterDataPermission?.add : true, [masterDataPermission]);
  const hasDeletePermission = useMemo(() => isCamBom ? masterDataPermission?.delete : true, [masterDataPermission]);
  // const hasUpdatePermission = useMemo(() => isCamBom ? masterDataPermission?.update : true, [masterDataPermission]);

  // Define Table Columns
  const columns = [
    {
      accessorKey: "tagName",
      header: "Tag Name",
      accessorFn: (row: any) => String(row?.tagName || ""),
    },
    {
      accessorKey: "displayLabel",
      header: "Display Label",
      accessorFn: (row: any) => String(row?.displayLabel || ""),
    },
    {
      accessorKey: "licenseScope",
      header: "License Scope",
      accessorFn: (row: any) => String(row?.licenseScope || ""),
    },
    {
      accessorKey: "helperText",
      header: "Helper Text",
      accessorFn: (row: any) => String(row?.helperText || ""),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: (row: any) => (
        <div
          className="flex items-center space-x-2"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <Switch
            id="active"
            onCheckedChange={(isActive: any) => {
              console.log("🚀 ~ usePropertyZone ~ isActive:", isActive);
              // onSetActiveConfig(isActive, row?.row?.original?.pricingId);
            }}
            checked={row.row.original.status == "active"}
          />
        </div>
      ),
    },
    ...(isOwner || !hasDeletePermission
      ? []
      : [
          {
            id: "actions",
            header: "Actions",
            cell: (row: any) => {
              return (
                <DropdownMenu>
                  <DropdownMenuTrigger>
                    <MoreVertical className="w-5 h-5 cursor-pointer" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="bg-white border-0 shadow-md"
                  >
                    <DropdownMenuItem
                      className="flex items-center gap-2"
                      onClick={() => {
                        setDeletedRow(row.row.original);
                        setOpenDeleteDialog(true);
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            },
          },
        ]),
  ];

  const { mutate: _deleteInfo, isPending: isDeletePropertyZonePending } =
    useDeletePropertyZoneMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Property Zone Deleted Successfully");
        queryClient.invalidateQueries({
          queryKey: ["property-zones"],
        });
        setOpenDeleteDialog(false);
      },
    });

  const onAdd = () => {
    navigate(`${routes.add}`);
  };

  const onDeleteConfirm = () => {
    // deleteInfo({ id: deletedRow?.zoneId });
  };

  return {
    onAdd,
    isMobile,
    globalFilter,
    setGlobalFilter,
    columns,
    propertyZones,
    isLoadingPropertyZones,
    openDeleteDialog,
    setOpenDeleteDialog,
    deletedRow,
    isDeletePropertyZonePending,
    onDeleteConfirm,
    isOwner,
    hasAddPermission
  };
};
export default usePropertyZone;
